import { useState } from "react"
import DashboardLayout from "@pma/app/dashboard/layout"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@pma/components/common/card"
import { Link, useParams } from "react-router-dom"
import { Avatar, AvatarFallback } from "@pma/components/common/avatar"
import { Button } from "@pma/components/common/button"
import { Building, Edit } from "lucide-react"
import {
  CONTRACTOR_COMPANY_BY_ID_ENDPOINT,
  CONTRACTOR_COMPANY_EDIT_ENDPOINT,
  FINANCIAL_SERVER_URL,
  PRICE_LIST_ALL_ENDPOINT,
} from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useAuth } from "@pma/stores/auth-store"
import { DictType } from "@pma/types"
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { format } from "date-fns"
import { he } from "date-fns/locale"
import { FINANCIAL_PRICE_LIST_PAGE_ROUTE } from "@pma/utils/page-routes"
import AddPriceList from "@pma/components/financials/add-price-list"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs"
import { cn } from "@pma/utils/helpers"
import { ContractorMainBoQTable } from "@pma/components/financials/contractor-main-boqs-table"
import PmsLoader from "@pma/components/common/loader"
import UnauthorizedCard from "@pma/components/layout/unauthorized-card"

const ContractorsDetailPage = () => {
  const [newName, setNewName] = useState("")
  const [isEditing, setIsEditing] = useState(false)

  const params = useParams()
  const { authConfig, setTabManager } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  const orgId = authConfig?.last_organization_id

  const companyId = params?.companyId
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const allStateTabs = authConfig?.tabs ?? {}
  const contractorActiveTab = allStateTabs ? allStateTabs["contractor"] : undefined
  const activeTabs: DictType = contractorActiveTab || {
    main: "price-list",
    sub: undefined,
  }

  const getWorkspaceContractors = async () => {
    const response = await axios.get(
      FINANCIAL_SERVER_URL + CONTRACTOR_COMPANY_BY_ID_ENDPOINT + `${companyId}`,
      {
        params: {
          company_id: companyId,
          organization_id: orgId,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  }

  const {
    data: company,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["workspace-contractors", companyId],
    queryFn: getWorkspaceContractors,
  })

  const getCompanyPriceList = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + PRICE_LIST_ALL_ENDPOINT,
      {
        organization_id: orgId,
        show_deleted: false,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: priceLists,
    refetch: refetchPriceLists,
    isError,
    error,
  } = useQuery({
    queryKey: ["company-price-list", companyId],
    queryFn: getCompanyPriceList,
  })

  const editCompany = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + CONTRACTOR_COMPANY_EDIT_ENDPOINT,
      {
        name: newName,
        company_id: companyId,
        organization_id: orgId,
      },
      {
        params: {
          organization_id: orgId,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const editCompanyMutation = useMutation({
    mutationKey: ["edit-company"],
    mutationFn: editCompany,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Company edited successfully"),
        variant: "success",
      })
      setIsEditing(false)

      refetch()
    },
  })

  const priceListError = error as DictType
  return (
    <DashboardLayout>
      <Card className='relative mt-2 max-w-full overflow-x-auto border-none p-2 shadow-none md:p-4'>
        <CardContent className='mt-0 h-auto min-h-[50vh] w-full overflow-x-hidden p-0'>
          <div className='space-y-6'>
            <div className='flex flex-col justify-between gap-4 md:flex-row'>
              <Card className='flex-1 py-2'>
                <CardHeader className='pb-2'>
                  <div className='flex items-start justify-between'>
                    <div className='flex items-center gap-4'>
                      <Avatar className='h-16 w-16 border'>
                        <AvatarFallback className='bg-primary/10 text-xl text-primary'>
                          {company?.name
                            .split(" ")
                            .map((n: string) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        {isEditing ? (
                          <div className='flex items-center gap-2'>
                            <input
                              className='mb-1 flex-1 rounded border bg-white/20 px-2 font-normal outline-none focus:border-primary'
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              autoFocus
                              onBlur={() => {
                                if (newName === company?.name) {
                                  setIsEditing(false)
                                  return
                                }
                                editCompanyMutation.mutate(company?.id)
                              }}
                              onKeyDown={(e) => {
                                if (e.key !== "Enter" || newName === company?.name) {
                                  setIsEditing(false)
                                  return
                                }
                                editCompanyMutation.mutate(company?.id)
                              }}
                            />
                            <Building size={18} />
                          </div>
                        ) : (
                          <CardTitle className='text-xl'>{company?.name}</CardTitle>
                        )}
                      </div>
                    </div>
                    <div className='flex gap-2'>
                      <Button
                        onClick={() => setIsEditing(!isEditing)}
                        variant='outline'
                        size='sm'
                        asChild
                      >
                        <Edit className='mr-1 h-4 w-4' />
                        {translate("Edit")}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </div>
            <div className='mt-4'>
              <Card className='border-none py-6 shadow-none'>
                <CardContent className=''>
                  <Tabs defaultValue={activeTabs?.main} className='w-full'>
                    <TabsList className='flex w-full items-center justify-center bg-transparent'>
                      <TabsTrigger
                        value='price-list'
                        className={cn(
                          "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                        )}
                        onClick={() => {
                          setTabManager({
                            group: "contractor",
                            main: "price-list",
                            sub: undefined,
                          })
                        }}
                      >
                        {translate("Price Lists")}
                      </TabsTrigger>
                      <TabsTrigger
                        className={cn(
                          "rounded-none border-primary bg-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                        )}
                        value='boq'
                        onClick={() => {
                          setTabManager({
                            group: "contractor",
                            main: "boq",
                            sub: undefined,
                          })
                        }}
                      >
                        {translate("Bill of Quantities")}
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value='price-list' className='mt-4'>
                      {isError ? (
                        <div className='flex items-center'>
                          <UnauthorizedCard message={priceListError?.response?.data?.message} />
                        </div>
                      ) : (
                        <Card>
                          <CardHeader>
                            <CardTitle>{translate("Price lists")}</CardTitle>
                            <CardDescription className='w-full'>
                              <div className='flex w-full items-center justify-between'>
                                <p>
                                  {translate("Price lists associated with")} {company?.name}
                                </p>
                                <AddPriceList
                                  companyId={companyId}
                                  revalidate={refetchPriceLists}
                                />
                              </div>
                            </CardDescription>
                          </CardHeader>
                          <CardContent className='grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-6'>
                            {priceLists?.map((priceList: DictType) => (
                              <Link
                                to={FINANCIAL_PRICE_LIST_PAGE_ROUTE + `/${priceList?.id}`}
                                key={priceList.id}
                              >
                                <Card className={cn(priceList.deleted ? "bg-red-100" : "")}>
                                  <CardHeader className='font-medium'>{priceList?.name}</CardHeader>

                                  <CardFooter className='flex justify-end text-sm'>
                                    {format(priceList?.created_at, "dd MMM yyy", {
                                      locale: isHebrew ? he : undefined,
                                    })}
                                  </CardFooter>
                                </Card>
                              </Link>
                            ))}

                            {isLoading && (
                              <div className='col-span-3 flex w-full items-center justify-center py-4 text-sm font-medium'>
                                <PmsLoader />
                              </div>
                            )}
                            {priceLists?.length < 1 && !isLoading && (
                              <div className='col-span-3 flex w-full items-center justify-center py-4 text-sm font-medium'>
                                <p>{translate("No items found")}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    <TabsContent value='boq' className='mt-4'>
                      <ContractorMainBoQTable contractorId={String(companyId)} />
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}

export default ContractorsDetailPage
